import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import EntHero from '../../components/enterprise/EntHero';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import EntToolbar from '../../components/enterprise/EntToolbar';
import EntPerson from '../../components/enterprise/EntPerson';
import EntConfirmDialog from '../../components/enterprise/EntConfirmDialog';
import { ENTERPRISE_ADMIN, ENTERPRISE_USER, roleLabel, roleOf } from '../../utils/companyRoles';
import { formatDate, seatLabel } from '../../utils/enterpriseUi';

function apiMessage(err, fallback) {
  return err.response?.data?.message || err.response?.data?.error || fallback;
}

export default function EnterpriseMembersPage() {
  const { enterpriseId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { current, companyById, refresh } = useWorkspace();
  const company = companyById(enterpriseId);
  const [members, setMembers] = useState([]);
  const [capabilities, setCapabilities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);

  const load = useCallback(async () => {
    const [membersRes, capRes] = await Promise.all([
      api.get('/api/enterprise/members', { params: { enterpriseId } }),
      api.get('/api/enterprise/capabilities', { params: { enterpriseId } }).catch(() => ({ data: {} })),
    ]);
    setMembers(membersRes.data.members || []);
    setCapabilities(capRes.data.capabilities || {});
  }, [enterpriseId]);

  useEffect(() => {
    if (!enterpriseId) return undefined;
    let cancelled = false;
    setLoading(true);
    load()
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load people');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, load]);

  const owner = members.find((m) => m.isOwner);
  const yourRole = roleLabel({
    role: company?.role || current?.role,
    isOwner: company?.isOwner || current?.isOwner,
  });
  const canManageRoles = !!capabilities.canManageRoles;
  const canTransferOwnership = !!capabilities.canTransferOwnership;
  const canAssignPro = !!capabilities.canAssignPro;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return members.filter((m) => {
      if (roleFilter === 'owner' && !m.isOwner) return false;
      if (roleFilter === 'admin' && (roleLabel(m) !== 'Admin' || m.isOwner)) return false;
      if (roleFilter === 'manager' && roleLabel(m) !== 'Manager') return false;
      if (roleFilter === 'member' && roleLabel(m) !== 'Member') return false;
      if (!needle) return true;
      return `${m.name || ''} ${m.email || ''} ${m.teamName || ''}`.toLowerCase().includes(needle);
    });
  }, [members, query, roleFilter]);

  const sendReset = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      await api.post(
        `/api/enterprise/members/${resetTarget.userId}/send-password-reset`,
        {},
        { params: { enterpriseId } }
      );
      showSuccess(`Password reset email sent to ${resetTarget.email}.`);
      setResetTarget(null);
    } catch (err) {
      showError(apiMessage(err, 'Could not send password reset'));
    } finally {
      setResetBusy(false);
    }
  };

  const setRole = async (member, role) => {
    setBusyId(member.userId);
    try {
      await api.post(
        `/api/enterprise/members/${member.userId}/role`,
        { role },
        { params: { enterpriseId } }
      );
      showSuccess(role === ENTERPRISE_ADMIN ? 'Promoted to Admin.' : 'Demoted to Member.');
      await load();
    } catch (err) {
      showError(apiMessage(err, 'Could not change role'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmTransfer = async () => {
    if (!transferTarget) return;
    setBusyId(transferTarget.userId);
    try {
      await api.post(
        '/api/enterprise/transfer-ownership',
        { userId: transferTarget.userId, enterpriseId },
        { params: { enterpriseId } }
      );
      showSuccess('Ownership transferred.');
      setTransferTarget(null);
      await Promise.all([load(), refresh()]);
    } catch (err) {
      showError(apiMessage(err, 'Could not transfer ownership'));
    } finally {
      setBusyId(null);
    }
  };

  const assignPro = async (member) => {
    setBusyId(member.userId);
    try {
      await api.post('/api/enterprise/licences/assign', { userId: member.userId }, { params: { enterpriseId } });
      showSuccess('Pro licence assigned.');
      await load();
    } catch (err) {
      showError(apiMessage(err, 'Could not assign Pro'));
    } finally {
      setBusyId(null);
    }
  };

  const removePro = async (member) => {
    setBusyId(member.userId);
    try {
      await api.post('/api/enterprise/licences/remove', { userId: member.userId }, { params: { enterpriseId } });
      showSuccess('Pro licence removed.');
      await load();
    } catch (err) {
      showError(apiMessage(err, 'Could not remove Pro'));
    } finally {
      setBusyId(null);
    }
  };

  const rowActions = (m) => {
    const written = roleOf(m);
    const isPro = m.seatTier === 'team_pro';
    return (
      <>
        <button
          type="button"
          className="ent-btn-analytics"
          onClick={() => navigate(`/enterprise-app/${enterpriseId}/analytics?userId=${m.userId}`)}
        >
          Analytics
        </button>
        <button
          type="button"
          className="ent-btn-ghost ent-btn-sm"
          onClick={() => setResetTarget(m)}
        >
          Reset password
        </button>
        {canManageRoles && !m.isOwner && written !== ENTERPRISE_ADMIN ? (
          <button
            type="button"
            className="ent-btn-secondary ent-btn-sm"
            disabled={busyId === m.userId}
            onClick={() => setRole(m, ENTERPRISE_ADMIN)}
          >
            Promote to Admin
          </button>
        ) : null}
        {canManageRoles && !m.isOwner && written !== ENTERPRISE_USER ? (
          <button
            type="button"
            className="ent-btn-ghost ent-btn-sm"
            disabled={busyId === m.userId}
            onClick={() => setRole(m, ENTERPRISE_USER)}
          >
            Demote to Member
          </button>
        ) : null}
        {canTransferOwnership && !m.isOwner ? (
          <button
            type="button"
            className="ent-btn-ghost ent-btn-sm"
            disabled={busyId === m.userId}
            onClick={() => setTransferTarget(m)}
          >
            Transfer ownership
          </button>
        ) : null}
        {canAssignPro && !isPro ? (
          <button
            type="button"
            className="ent-btn-secondary ent-btn-sm"
            disabled={busyId === m.userId}
            onClick={() => assignPro(m)}
          >
            Assign Pro
          </button>
        ) : null}
        {canAssignPro && isPro ? (
          <button
            type="button"
            className="ent-btn-ghost ent-btn-sm"
            disabled={busyId === m.userId}
            onClick={() => removePro(m)}
          >
            Remove Pro
          </button>
        ) : null}
      </>
    );
  };

  const hero = (
    <EntHero
      title="Account"
      lead="Company details and the people in this workspace. Offboarding and seat changes live on Seats."
    />
  );

  if (loading) {
    return (
      <div className="ent-content">
        {hero}
        <EntSkeleton variant="block" />
        <EntSkeleton variant="rows" count={5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="ent-content">
        {hero}
        <div className="ent-flash ent-flash-error" role="alert">{error}</div>
      </div>
    );
  }

  return (
    <div className="ent-content">
      {hero}

      <section className="ent-section">
        <h2 className="ent-section-title">Company</h2>
        <div className="ent-panel">
          <dl className="ent-dl">
            <dt>Name</dt>
            <dd>{company?.name || (current?.id === enterpriseId ? current.name : '—')}</dd>
            <dt>Your role</dt>
            <dd>{yourRole}</dd>
            <dt>Owner</dt>
            <dd>{owner ? `${owner.name || '—'} · ${owner.email}` : '—'}</dd>
            <dt>People</dt>
            <dd>{members.length}</dd>
          </dl>
          <p className="ent-helper">
            Company name is managed with Velox. Ownership can be transferred from a person below if you are the owner.
          </p>
        </div>
      </section>

      <section className="ent-section">
        <div className="ent-section-header">
          <h2 className="ent-section-title">People</h2>
        </div>
        <EntToolbar
          search={query}
          onSearch={setQuery}
          searchPlaceholder="Search name, email or team"
          searchLabel="Search people"
        >
          <select
            className="ent-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            aria-label="Filter by role"
          >
            <option value="all">All roles</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="member">Member</option>
          </select>
        </EntToolbar>

        {members.length === 0 ? (
          <EntEmptyState
            title="No people yet"
            lead="Invite colleagues from Invitations. Once they accept, they appear here."
            action={
              <button
                type="button"
                className="ent-btn-primary"
                onClick={() => navigate(`/enterprise-app/${enterpriseId}/invites`)}
              >
                Go to Invitations
              </button>
            }
          />
        ) : visible.length === 0 ? (
          <EntEmptyState title="No people match" lead="Try a different search or role filter." />
        ) : (
          <>
            <div className="ent-table-wrap ent-hide-sm">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th>Team</th>
                    <th>Seat</th>
                    <th>Joined</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((m) => (
                    <tr key={m._id}>
                      <td><EntPerson name={m.name} email={m.email} /></td>
                      <td>
                        <span className={`ent-chip ${m.status === 'active' ? 'ent-chip-active' : 'ent-chip-suspended'}`}>
                          {roleLabel(m)}
                        </span>
                      </td>
                      <td>{m.teamName || '—'}</td>
                      <td>{seatLabel(m.seatTier)}</td>
                      <td>{formatDate(m.joinedAt)}</td>
                      <td className="ent-actions-cell">{rowActions(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ent-stack">
              {visible.map((m) => (
                <div key={m._id} className="ent-stack-card ent-stack-card-static">
                  <EntPerson name={m.name} email={m.email} />
                  <div className="ent-stack-meta">
                    <span>{roleLabel(m)}</span>
                    <span>{m.teamName || 'No team'}</span>
                    <span>{seatLabel(m.seatTier)}</span>
                  </div>
                  <div className="ent-stack-actions">{rowActions(m)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <EntConfirmDialog
        open={!!resetTarget}
        title="Send password reset?"
        message={
          resetTarget
            ? `Send a password reset email to ${resetTarget.email}? They will be able to choose a new password from the link.`
            : ''
        }
        confirmLabel="Send email"
        busy={resetBusy}
        onConfirm={sendReset}
        onClose={() => !resetBusy && setResetTarget(null)}
      />

      <EntConfirmDialog
        open={!!transferTarget}
        title="Transfer ownership?"
        message={
          transferTarget
            ? `Make ${transferTarget.name || transferTarget.email} the owner of this company?`
            : ''
        }
        detail="You will remain an Admin. Only the new owner can change billing, capacity, and who owns the workspace. This cannot be undone from here."
        confirmLabel="Transfer ownership"
        danger
        busy={busyId === transferTarget?.userId}
        onConfirm={confirmTransfer}
        onClose={() => !busyId && setTransferTarget(null)}
      />
    </div>
  );
}
